import {
    MenuItem,
    Select,
    OutlinedInput,
    InputAdornment,
    Button,
    Link,
    Typography,
    SelectChangeEvent,
    Stack,
} from "@mui/material";
import React from "react";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CustomerDetailsPanel from "../../../../../Components/Panels/CustomerDetailsPanel/customer-panel";
import { useToggle } from "../../../../../Utils/Hooks/useToggle";
import { ModalContext } from "../../../../../Components/Modals";
import ModalAddCustomer, {
    ModalAddCustomerElementProps,
} from "../../../../../Components/Modals/ModalAddCustomer/modal-add-customer";
import ListPageHeader, {
    ListPageHeaderGroupActions,
    ListPageHeaderRight,
} from "../../../../../Components/Misc/ListPageHeader";
import { orderService } from "../../../../../Core/Api/services";
import { DbCustomerInfos } from "../../../../../Core/Api/Types/db-customer-infos";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid";
import TableCellEnabled from "../../../../../Components/Table/TableCellEnabled/table-cell-enabled";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ModalConfirmAction, {
    ModalConfirmActionElementProps,
} from "../../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";
import CheckIcon from "@mui/icons-material/Check";
import { routePaths } from "../../../../../Router/router";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import DashboardLayout from "../../../../../Layouts/Dashboard/dashboard";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GlobalConst from "../../../../../Core/Utils/Constants/globals";
import TableCellTypography, {
    TableCellLongTypography,
} from "../../../../../Components/Table/TableCellTypography/table-cell-typography";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../../Components/Table/table-uuids";

function Customers() {
    const { t } = useTranslation();
    const { active: customerDetailsPanelOpen, toggle: toggleCustomerDetailsPanelOpen } = useToggle(false);
    const [customers, setCustomers] = React.useState<DbCustomerInfos[]>([]);
    const [selectedCustomer, setSelectedCustomer] = React.useState<DbCustomerInfos>();
    const [enable, setEnable] = React.useState<string[]>([]);
    const [filterEnable, setFilterEnable] = React.useState<string>("Default");
    const [addedManu, setAddedManu] = React.useState<string[]>([]);
    const [filterAddedManu, setFilterAddedManu] = React.useState<string>("Default");

    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const customSnackbar = useCustomSnackbar();
    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };
    const apiGrid = useGridApiRef();

    // Filter
    React.useEffect(() => {
        setEnable(Object.values(["Enabled", "Disabled"]));
        setAddedManu(Object.values(["True", "False"]));
    }, []);

    // Fetching customers
    React.useEffect(() => {
        orderService.GetCustomersList().then((response) => {
            if (response.customers) {
                setCustomers(response.customers);
                setIsLoading(false);
            }
        });
    }, []);

    // Table creation
    const rows: GridRowsProp = customers;
    type Row = (typeof rows)[number];
    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "Id",
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: Row) => (
                <Link
                    href={`${routePaths.customers.path}/${data.value}`}
                    color="primary.text"
                    underline="hover"
                    sx={{ fontSize: 12 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    #{data.value}
                </Link>
            ),
        },
        {
            field: "name",
            headerName: t("common:name"),
            minWidth: CellSize.xlarge,
            flex: CellSize.xlarge,
            renderCell: (data: Row) => <TableCellTypography data={data.value} color="text.primary" fontWeight={500} />,
        },
        {
            field: "address",
            headerName: t("common:address"),
            minWidth: CellSize.xlarge,
            flex: CellSize.xlarge,
            type: "string",
            valueGetter: (data: Row, row: any) =>
                `${row?.address + ", " || ""}${row?.zipCode + " " || ""}${row?.city + " - " || ""}${row?.country || ""}`,
            renderCell: (data: Row) => (
                <TableCellLongTypography
                    variant="body1"
                    color="text.secondary"
                    data={data?.row?.address + ", " + data?.row?.zipCode + " " + data?.row?.city + " - " + data?.row?.country}
                />
            ),
        },
        {
            field: "customerCodeId",
            headerName: t("field:label.externalID"),
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            renderCell: (data: Row) => (
                <Typography color="text.secondary" variant="body1">
                    #{data.value}
                </Typography>
            ),
        },
        {
            field: "addedManually",
            headerName: t("table:headerName.system"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            type: "boolean",
            valueGetter: (data: Row, row: any) => !row?.addedManually,
            valueFormatter: (params: any, row: any) => {
                if (params !== undefined) {
                    return params;
                } else return "";
            },
            renderCell: (data: Row) => <TableCellEnabled value={data.value} />,
        },
        {
            field: "disabled",
            headerName: t("common:enability"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            type: "boolean",
            valueGetter: (data: Row, row: any) => !row?.disabled,
            valueFormatter: (params: any, row: any) => {
                if (params !== undefined) {
                    return params;
                } else return "";
            },
            renderCell: (data: Row) => <TableCellEnabled value={data.value} />,
        },
        {
            field: "actions",
            headerName: "",
            type: "actions",
            align: "right",
            minWidth: CellSize.actionsSmall,
            flex: CellSize.actionsSmall,
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<EditOutlinedIcon />}
                    label={t("common:editUpperCase")}
                    onClick={() => handleEditCustomer(params.row)}
                    showInMenu
                />,
                <GridActionsCellItem
                    icon={<DeleteOutlineIcon />}
                    label={t("common:delete")}
                    color="error"
                    onClick={() => handleDeleteCustomer(params.row)}
                    showInMenu
                />,
                <GridActionsCellItem
                    icon={<MoreHorizIcon />}
                    label={t("table:cellLabel.moreInfos")}
                    onClick={() => openPanelForDetailsMenuCall(params.row)}
                    showInMenu
                />,
            ],
        },
    ];

    const handleNewCustomerClick = React.useCallback(() => {
        if (!openModal) {
            return;
        }

        const onClose: ModalAddCustomerElementProps["onClose"] = (data) => {
            console.log(data);
        };

        openModal<ModalAddCustomerElementProps>(ModalAddCustomer, { onClose, context: "Create" });
    }, [openModal]);

    const handleEditCustomer = React.useCallback(
        (customer) => {
            if (!openModal) {
                return;
            }
            const onClose: ModalAddCustomerElementProps["onClose"] = (data) => {
                console.log(data);
            };
            openModal<ModalAddCustomerElementProps>(ModalAddCustomer, { onClose, context: "Edit", customer: customer });
        },
        [openModal]
    );

    const openPanelForDetails = (e: GridRowParams) => {
        setSelectedCustomer(e.row);
        if (!customerDetailsPanelOpen) toggleCustomerDetailsPanelOpen();
    };

    const openPanelForDetailsMenuCall = (row) => {
        setSelectedCustomer(row);
        if (!customerDetailsPanelOpen) toggleCustomerDetailsPanelOpen();
    };

    //delete provider
    const handleDeleteCustomer = (customer) => {
        setSelectedCustomer(customer);
        if (!openModal) return;
        openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
            title: t("modal:title.deleteThisCustomer"),
            commentLabel: t("modal:comment.confirmDeleteCustomer"),
            onConfirm: HandleDeleteCustomerConfirm,
        });
    };

    const HandleDeleteCustomerConfirm = () => {
        if (selectedCustomer)
            orderService.DeleteCustomer(selectedCustomer.id).then((response) => {
                if (response.success) customSnackbar(t("alert:success.providerDeleted"), "success");
            });
    };

    const handleFilterEnable = (event: SelectChangeEvent<string>) => {
        setFilterEnable(event.target.value as string);
    };

    const handleFilterAddedMan = (event: SelectChangeEvent<string>) => {
        setFilterAddedManu(event.target.value as string);
    };

    const additionalFilters = (
        <>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={filterEnable}
                onChange={(value) => handleFilterEnable(value)}
            >
                <MenuItem value={"Default"}>{t("common:enability")}</MenuItem>
                {enable.map((status, index) => (
                    <MenuItem value={status} key={index}>
                        {status}
                    </MenuItem>
                ))}
            </Select>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={filterAddedManu}
                onChange={(value) => handleFilterAddedMan(value)}
            >
                <MenuItem value={"Default"}>{t("menu:title.addedManually")}</MenuItem>
                {addedManu.map((status, index) => (
                    <MenuItem value={status} key={index}>
                        {status}
                    </MenuItem>
                ))}
            </Select>
        </>
    );

    return (
        <DashboardLayout noScroll sxContent={{ px: 2 }}>
            <DataTable
                loading={isLoading}
                tableKey={TableUuids.CUSTOMERS_TABLE}
                rows={customers.filter(
                    (customer) =>
                        (filterEnable === "Default" ||
                            (customer.disabled === false && filterEnable === "Enabled") ||
                            (customer.disabled === true && filterEnable === "Disabled")) &&
                        (filterAddedManu === "Default" ||
                            (customer.addedManually === false && filterAddedManu === "False") ||
                            (customer.addedManually === true && filterAddedManu === "True"))
                )}
                initialState={{ pinnedColumns: { left: ["id", "name"], right: ["actions"] } }}
                columns={columns}
                additionalFilters={additionalFilters}
                additionalActions={
                    <Button startIcon={<AddIcon />} color="primary" variant="contained" onClick={handleNewCustomerClick}>
                        {t("button:newCustomer")}
                    </Button>
                }
                disableRowSelectionOnClick
                onRowClick={openPanelForDetails}
                apiRef={apiGrid}
            />
            <CustomerDetailsPanel
                open={customerDetailsPanelOpen}
                onClose={toggleCustomerDetailsPanelOpen}
                customer={selectedCustomer!}
            />
        </DashboardLayout>
    );
}

export default Customers;
